import React, {
	useState,
} from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon';

// Import styles to match Library design
import styles from './VisualContentLoop.module.css';
import LoopAssignments from './VisualContentLoopAssignments';
import VisualContentLoopModal from './VisualContentLoopModal';
import API from 'Vulture/lib/API';
import VisualContentLoopsTableView from './VisualContentLoopsTable';
import VisualContentLoopView from './VisualContentLoop';
import {
	SaveButton,
	setGlobalVisualContentLoop,
	useVisualContentLoopContext,
} from './VisualContentLoopContext';
import {
	vclDeleteValidation,
} from 'Vulture/VisualContentLoop';
import {
	handlePendingChangesNavigation,
} from 'Vulture/lib/Nav';
import {
	broncoURL,
} from 'Vulture/ENV';
import {
	calculateVCLTotals,
} from './utils/vclCalculations';

import { DuplicateButton, DuplicateModal } from './DuplicateComponents';

const VCLDuplicateButton = ({ onOpenModal }) => {
	const {
		handleSave,
		hasChanges,
		isSaving,
		isRefreshing,
	} = useVisualContentLoopContext();

	return (
		<DuplicateButton
			onOpenModal={onOpenModal}
			hasChanges={hasChanges}
			handleSave={handleSave}
			isSaving={isSaving || isRefreshing}
			itemName="Loop"
		/>
	);
};

const VisualContentLoopsBreadCrumbs = (props) => {
	const {
		app,
		visualContentLoop,
		visualContentLoopName,
		clearVisualContentLoop,
	} = props;

	const vclContext = useVisualContentLoopContext();

	return (
		<div style={{
			marginBottom: '20px',
		}}>
			<div style={{
				fontSize: '14px',
				color:    '#666',
			}}>
				<span
					style={visualContentLoop ? {
						cursor: 'pointer',
						color:  '#007bff',
					} : {
						fontWeight: 'bold',
					}}
					onClick={() => {
						handlePendingChangesNavigation(app, clearVisualContentLoop, vclContext);
					}}
				>
					VCL Home
				</span>
				{visualContentLoop && (
					<>
						<span style={{
							margin: '0 5px',
						}}>/</span>
						<span style={{
							fontWeight: 'bold',
						}}>{visualContentLoopName}</span>
					</>
				)}
			</div>
		</div>
	);
};

class CrowFieldCustomVultureVisualContentLoops extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			confirmModal:          false,
			confirmModalContent:   '',
			loading:               false,
			showAssignments:       false,
			visualContentLoops:    [],
			visualContentLoop:     null,
			isEditingTitle:        false,
			visualContentLoopName: '',
		};

		this.abortControllerRef = React.createRef();
	}

	async fetchVisualContentLoopInfo(visualContentLoopID) {
		const {
			accountUUID,
		} = this.props;

		try {
			// Use /providers endpoint instead of /info to get raw provider data
			// This allows us to calculate slots/duration client-side using shouldSkipSlide
			const response = await fetch(`${broncoURL}/visualContentLoop/${visualContentLoopID}/providers`, {
				method:  'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					accountUUID,
					systemUUID: 'system', // Use default system for table view
				}),
			});
			const data = await response.json();

			// Use modularized calculation logic that matches VisualContentLoop component
			const {
				totalSlots, totalDuration,
			} = calculateVCLTotals(data.providers, data.dynamic_durations);

			// Return data in same format as old /info endpoint
			return {
				slots:             totalSlots,
				total_duration:    totalDuration,
				resolution:        data.resolution || '',
				dynamic_durations: data.dynamic_durations || {},
			};
		} catch (error) {
			console.error('Failed to fetch visual content loop info:', error);
			return null;
		}
	}

	componentDidMount() {
		this.loadVisualContentLoops();

		// Initialize the global visual content loop
		setGlobalVisualContentLoop(this.state.visualContentLoop);

		window.__vclReset = () => {
			if (this.state.visualContentLoop || this.state.showAssignments) {
				this.setState({
					visualContentLoop:     null,
					visualContentLoopName: '',
					isEditingTitle:        false,
					showAssignments:       false,
				});
			}
		};
	}

	componentDidUpdate(_, prevState) {
		// When navigating back to table view from detail view, reload the table
		if (prevState.visualContentLoop && !this.state.visualContentLoop) {
			this.loadVisualContentLoops();
		}

		if (this.state.visualContentLoop !== prevState.visualContentLoop) {
			// Update the global visual content loop in the provider
			setGlobalVisualContentLoop(this.state.visualContentLoop);
		}
	}

	componentWillUnmount() {
		// Cleanup abort controller on unmount
		if (this.abortControllerRef.current) {
			this.abortControllerRef.current.abort();
		}

		if (window.__vclReset) {
			try {
				delete window.__vclReset;
			} catch (e) { /* noop */ }
		}
	}

	loadVisualContentLoops = async () => {
		this.abortControllerRef.current = new AbortController();

		this.setState({
			loading: true,
		});

		try {
			const response = await API.findRecords('visual_content_loop', {}, this.abortControllerRef.current.signal);

			if (response && response && Array.isArray(response)) {
				const apiVisualContentLoops = response.map((VCL) => {
					return {
						id:            VCL.id,
						uuid:          VCL.uuid || VCL.id,
						name:          VCL.name || 'Untitled Visual Content Loop',
						created:       VCL.created || VCL.created_at || new Date().toISOString().split('T')[0],
						providers:     0,
						slots:         0,
						resolution:    '',
						totalDuration: 0,
						isChecked:     false,
					};
				}).sort((a, b) => {
					return a.name.localeCompare(b.name);
				});

				// Fetch info for each loop to populate the table
				await Promise.all(apiVisualContentLoops.map(async (loop) => {
					const info = await this.fetchVisualContentLoopInfo(loop.id);

					if (info) {
						loop.providers = info.slots || 0;
						loop.slots = info.slots || 0;
						loop.resolution = info.resolution || '';
						loop.totalDuration = info.total_duration || 0;
					}

					return loop;
				}));

				this.setState({
					visualContentLoops: apiVisualContentLoops,
					loading:            false,
				});
			} else {
				// Fall back to mock data if response doesn't have expected structure
				console.warn('API response did not contain expected data structure, falling back to mock data');
				this.setState({
					visualContentLoops: [],
					loading:            false,
				});
			}
		} catch (error) {
			// Check if the error is due to abort (component unmounted)
			if (error.name === 'AbortError') {
				console.log('API request was aborted');
				return;
			}

			// For any other error, fall back to mock data
			console.warn('Failed to load slide visual content loops from API, falling back to mock data:', error);
			this.setState({
				visualContentLoops: [],
				loading:            false,
			});
		}
	}

	loadVisualContentLoop = async (loopUUID) => {
		try {
			console.log('Fetching details for visual content loop ID:', loopUUID);
			const response = await API.findRecord('visual_content_loop', loopUUID, {
				includes: [
					'account',
				],
			});
			console.log('Visual content loop details response:', response);

			const info = await this.fetchVisualContentLoopInfo(loopUUID);
			if (info) {
				response.dynamic_durations = info.dynamic_durations;
				// Backend-computed effective resolution (VCL override > account default)
				response.effective_resolution = info.resolution;
			}

			this.setState({
				visualContentLoop:     response,
				visualContentLoopName: response.name || 'Visual Content Loop',
				isEditingTitle:        false,
			});
		} catch (error) {
			console.error('Failed to fetch visual content loop details:', error);

			this.setState({
				isEditingTitle: false,
			});
		}
	}

	deleteSelectedLoops = (selectedLoops) => {
		const {
			visualContentLoops,
		} = this.state;

		if (selectedLoops.length === 0) {
			return;
		}

		this.setState({
			confirmModal:        true,
			confirmModalContent: (
				<>
					<p>Are you sure you want to delete?</p>
					<div className="confirm-modal-buttons">
						<button className="button" data-test-id="confirmModalProceedButton" onClick={() => {
							let errorMsgs = [];

							const deletePromises = selectedLoops.map(async (loopUUID) => {
								try {
									const response = await API.deleteRecord('visual_content_loop', loopUUID);
									console.log('Delete visual content loop response:', response);
								} catch (error) {
									errorMsgs.push(error.toString());
									console.error('Failed to delete visual content loop via API:', error);
									throw error;
								}
							});

							Promise.allSettled(deletePromises).then(() => {
								if (errorMsgs.length === 0) {
									// Refresh from API to ensure authoritative table state
									this.setState({
										loading:             true,
										confirmModal:        false,
										confirmModalContent: '',
									}, () => {
										this.loadVisualContentLoops();
									});
								} else if (errorMsgs.length < 3) {
									this.setState({
										confirmModalContent: <>
											{errorMsgs.map((error, index) => {
												return (
													<p key={index}>{error}</p>
												);
											})}
											<div className="confirm-modal-buttons">
												<button className="button" onClick={() => {
													this.setState({
														confirmModal:        false,
														confirmModalContent: '',
													});
												}}>OK</button>
											</div>
										</>,
									});
								} else {
									this.setState({
										confirmModalContent: <>
											<p>{errorMsgs.length} errors found.</p>
											<p>{selectedLoops.length - errorMsgs.length} successes.</p>
											<div className="confirm-modal-buttons">
												<button className="button" onClick={() => {
													this.setState({
														confirmModal:        false,
														confirmModalContent: '',
													});
												}}>OK</button>
											</div>
										</>,
									});
								}
							});
						}}>Proceed</button>
						<button className="button" onClick={() => {
							this.setState({
								confirmModal:        false,
								confirmModalContent: '',
							});
						}}>Cancel</button>
					</div>
				</>
			),
		});
	}

	createNewLoop = () => {
		const {
			accountUUID,
			app,
		} = this.props;

		app.openModal({
			modalSize:  1,
			showModal:  true,
			modalProps: {
				title:          'Add Visual Content Loop',
				customClass:    'create-new-visual-content-loop-modal',
				pendingChanges: false,
				jsx:            (
					<VisualContentLoopModal
						accountUUID={accountUUID}
						createOrUpdateLoop={async (loopName) => {
							try {
								const newVisualContentLoopData = {
									name:    loopName,
									// Add any other required fields here
									account: {
										'uuid': accountUUID,
									},
								};

								console.log('Create new visual content loop with data:', newVisualContentLoopData);

								const response = await API.createRecord('visual_content_loop', newVisualContentLoopData);
								console.log('Create visual content loop response:', response);

								this.loadVisualContentLoops();
							} catch (error) {
								console.error('Failed to update visual content loop:', error);

								this.setState({
									confirmModal:        true,
									confirmModalContent: (
										<>
											<p>{error.toString()}</p>
											<div className="confirm-modal-buttons">
												<button className="button" onClick={() => {
													this.setState({
														confirmModal:        false,
														confirmModalContent: '',
													});
												}}>OK</button>
											</div>
										</>
									),
								});
							}
						}}

					/>
				),
			},
		});
	}

	openLoopAssignments = () => {
		const {
			visualContentLoops,
		} = this.state;

		if (visualContentLoops.length === 0) {
			this.setState({
				confirmModal:        true,
				confirmModalContent: (
					<>
						<p>There are no Loops to assign.</p>
						<div className="confirm-modal-buttons">
							<button className="button" onClick={() => {
								this.setState({
									confirmModal:        false,
									confirmModalContent: '',
								});
							}}>OK</button>
						</div>
					</>
				),
			});
		} else {
			this.setState({
				showAssignments: true,
			});
		}
	}

	closeAssignments = () => {
		this.setState({
			showAssignments: false,
			visualContentLoop: null,
		});
	}

	editLoop = async (loopUUID, loopName) => {
		const {
			accountUUID,
		} = this.props;

		try {
			const visualContentLoopData = {
				name:    loopName,
				// Add any other required fields here
				account: {
					'uuid': accountUUID,
				},
			};

			console.log('Update visual content loop with data:', visualContentLoopData);

			const response = await API.updateRecord('visual_content_loop', loopUUID, visualContentLoopData);
			console.log('Update visual content loop response:', response);

			this.loadVisualContentLoops();

			this.setState({
				visualContentLoopName: response.name,
			});
		} catch (error) {
			console.error('Failed to update visual content loop:', error);

			this.setState({
				confirmModal:        true,
				confirmModalContent: (
					<>
						<p>{error.toString()}</p>
						<div className="confirm-modal-buttons">
							<button className="button" onClick={() => {
								this.setState({
									confirmModal:        false,
									confirmModalContent: '',
								});
							}}>OK</button>
						</div>
					</>
				),
			});
		}
	}

	startEditingTitle = () => {
		this.setState({
			isEditingTitle: true,
		});
	};

	cancelEditingTitle = () => {
		const {
			visualContentLoop,
		} = this.state;
		this.setState({
			isEditingTitle:        false,
			visualContentLoopName: (visualContentLoop && (visualContentLoop.name || visualContentLoop.loop_name)) || '',
		});
	};

	handleTitleChange = (e) => {
		this.setState({
			visualContentLoopName: e.target.value,
		});
	};

	saveTitleEdit = () => {
		const {
			visualContentLoopName, visualContentLoop,
		} = this.state;

		this.setState({
			isEditingTitle: false,
		});

		if (!visualContentLoop) return;

		const currentName = visualContentLoop.name || visualContentLoop.loop_name;
		if (currentName === visualContentLoopName) return; // no change

		this.editLoop(visualContentLoop.id || visualContentLoop.uuid, visualContentLoopName);
	};

	handleTitleKeyPress = (e) => {
		if (e.key === 'Enter') {
			this.saveTitleEdit();
		} else if (e.key === 'Escape') {
			this.cancelEditingTitle();
		}
	};

	editLoopModal = (loopUUID) => {
		const {
			accountUUID,
			app,
		} = this.props;

		const loop = this.state.visualContentLoops.find((l) => {
			return l.uuid === loopUUID;
		});

		app.openModal({
			modalSize:  1,
			showModal:  true,
			modalProps: {
				title:          'Edit Visual Content Loop',
				customClass:    'edit-visual-content-loop-modal',
				pendingChanges: false,
				jsx:            (
					<VisualContentLoopModal
						accountUUID={accountUUID}
						loopData={loop}
						createOrUpdateLoop={(loopName) => {
							this.editLoop(loopUUID, loopName);
						}}
					/>
				),
			},
		});
	}

	deleteSingleLoop = (loopUUID) => {
		const {
			accountUUID,
		} = this.props;

		const {
			visualContentLoops,
		} = this.state;

		const loop = visualContentLoops.find((l) => {
			return l.uuid === loopUUID;
		});

		vclDeleteValidation(loopUUID, accountUUID)
			.then((componentCount) => {
				if (componentCount === 0) {
					this.setState({
						confirmModal:        true,
						confirmModalContent: (
							<>
								<p>{`Are you sure you want to delete ${loop.name}?`}</p>
								<div className="confirm-modal-buttons">
									<button className="button" data-test-id="confirmModalDeleteButton" onClick={async () => {
										try {
											const response = await API.deleteRecord('visual_content_loop', loopUUID);
											console.log('Delete visual content loop response:', response);
										} catch (error) {
											console.error('Failed to delete visual content loop via API:', error);
											this.setState({
												confirmModalContent: <>
													<p>{error.toString()}</p>
													<div className="confirm-modal-buttons">
														<button className="button" onClick={() => {
															this.setState({
																confirmModal:        false,
																confirmModalContent: '',
															});
														}}>OK</button>
													</div>
												</>,
											});

											return;
										}

										// Perform deletion
										const {
											visualContentLoops,
										} = this.state;
										const newVisualContentLoops = visualContentLoops.filter((l) => {
											return l.uuid !== loopUUID;
										});

										this.setState({
											visualContentLoops:  newVisualContentLoops,
											confirmModal:        false,
											confirmModalContent: '',
										});
									}}>Delete</button>
									<button className="button" onClick={() => {
										this.setState({
											confirmModal:        false,
											confirmModalContent: '',
										});
									}}>Cancel</button>
								</div>
							</>
						),
					});
				} else {
					this.setState({
						confirmModal:        true,
						confirmModalContent: (
							<>
								<p>{`${loop.name} is assigned to ${componentCount === 1 ? 'a System' : `${componentCount} Systems`}.`}</p>
								<p>{`Please remove ${loop.name} from ${componentCount === 1 ? 'that System' : 'those Systems'}.`}</p>
								<div className="confirm-modal-buttons">
									<button className="button" onClick={() => {
										this.setState({
											confirmModal:        false,
											confirmModalContent: '',
										});
									}}>OK</button>
								</div>
							</>
						),
					});
				}

			})
			.catch((error) => {
				this.setState({
					confirmModal:        true,
					confirmModalContent: (
						<>
							<p>{`Network error, please try again later.`}</p>
							<p>{`${error}`}</p>
							<div className="confirm-modal-buttons">
								<button className="button" onClick={() => {
									this.setState({
										confirmModal:        false,
										confirmModalContent: '',
									});
								}}>OK</button>
							</div>
						</>
					),
				});
			});
	}

	clearVisualContentLoop = () => {
		this.setState({
			visualContentLoop: null,
			showAssignments:   false,
		});
	}

	openDuplicateModal = () => {
		const {
			app,
		} = this.props;

		app.openModal({
			modalSize:  1,
			showModal:  true,
			modalProps: {
				title:          'Duplicate Loop',
				customClass:    'duplicate-visual-content-loop-modal',
				pendingChanges: false,
				jsx:            (
					<DuplicateModal
						app={app}
						onDuplicate={this.performDuplicate}
						itemName="Loop"
					/>
				),
			},
		});
	}

	performDuplicate = async (count) => {
		const {
			visualContentLoop,
			visualContentLoops,
		} = this.state;

		const {
			accountUUID,
		} = this.props;

		if (!visualContentLoop) {
			return;
		}

		const sourceName = visualContentLoop.name || visualContentLoop.loop_name || 'Loop';

		const sourceRecord = await API.findRecord('visual_content_loop', visualContentLoop.id);

		const takenNames = new Set((visualContentLoops || []).map((l) => {
			return l.name || '';
		}));

		const getUniqueName = (base) => {
			if (!takenNames.has(base)) {
				takenNames.add(base);
				return base;
			}
			let idx = 2;
			while (takenNames.has(`${base} ${idx}`)) {
				idx++;
			}
			const uniqueName = `${base} ${idx}`;
			takenNames.add(uniqueName);
			return uniqueName;
		};

		const base = `${sourceName} Copy`;
		for (let i = 0; i < count; i++) {
			await API.createRecord('visual_content_loop', {
				name:      getUniqueName(base),
				providers: sourceRecord.providers,
				account:   {
					uuid: accountUUID,
				},
			});
		}

		// Return to table view; componentDidUpdate will handle reloading
		this.clearVisualContentLoop();
	}

	render() {
		const {
			app,
			accountUUID,
		} = this.props;

		const {
			confirmModal,
			confirmModalContent,
			showAssignments,
			loading,
			visualContentLoop,
			isEditingTitle,
			visualContentLoopName,
		} = this.state;

		if (showAssignments) {
			return <LoopAssignments
				accountUUID={accountUUID}
				visualContentLoops={this.state.visualContentLoops}
				onClose={this.closeAssignments}
			/>;
		}

		let confirmModalElement = '';
		if (confirmModal) {
			confirmModalElement = (
				<div className="confirm-modal-container">
					<div className="confirm-modal-content">
						{confirmModalContent}
					</div>
				</div>
			);
		}

		return (
			<div className="systems-page fade-in">
				{confirmModalElement}
				<div className={styles.pageHeader}>
					{visualContentLoop && (
						isEditingTitle ? (
							<div style={{
								display:    'flex',
								alignItems: 'center',
								gap:        '10px',
							}}>
								<h2 style={{
									margin: 0,
								}}>Visual Content Loop:</h2>
								<input
									type="text"
									value={visualContentLoopName}
									onChange={this.handleTitleChange}
									onKeyDown={this.handleTitleKeyPress}
									onBlur={this.saveTitleEdit}
									autoFocus
									style={{
										fontSize:     '1.5rem',
										fontWeight:   'bold',
										border:       '2px solid #007cba',
										borderRadius: '4px',
										padding:      '4px 8px',
										background:   '#fff',
										outline:      'none',
									}}
								/>
							</div>
						) : (
							<h2>
								Visual Content Loop:
								<span
									onClick={this.startEditingTitle}
									title='Click to edit loop name'
									style={{
										cursor:     'pointer',
										marginLeft: '10px',
										display:    'inline-flex',
										alignItems: 'center',
										gap:        '8px',
									}}
								>
									{visualContentLoopName}
									<CustomIcon
										icon='edit'
										style={{
											fontSize: '0.8rem',
											opacity:  0.6,
										}}
									/>
								</span>
							</h2>
						)
					)}
					{!visualContentLoop && (
						<h2>Visual Content Loops</h2>
					)}

					{visualContentLoop && (
						<div className={styles.pageHeaderButtons}>
							<VCLDuplicateButton onOpenModal={this.openDuplicateModal} />
							<button
								className="button"
								data-test-id="loopAssignmentsButton"
								onClick={this.openLoopAssignments}
								style={{
									display:    'flex',
									alignItems: 'center',
									gap:        '5px',
								}}
							>
								<CustomIcon icon="list" />
								Loop Assignments
							</button>
							<SaveButton location="header" />

						</div>
					)}
				</div>

				{/* Breadcrumb Navigation */}
				<VisualContentLoopsBreadCrumbs
					app={app}
					visualContentLoop={showAssignments ? null : visualContentLoop}
					visualContentLoopName={visualContentLoopName}
					clearVisualContentLoop={this.clearVisualContentLoop}
				/>

				{/* Loading Indicator */}
				{loading && (
					<div style={{
						display:        'flex',
						justifyContent: 'center',
						alignItems:     'center',
						padding:        '40px',
						fontSize:       '16px',
						color:          '#666',
					}}>
						<CustomIcon icon="spinner" style={{
							marginRight: '8px',
						}} />
						Loading visual content loops...
					</div>
				)}

				{!loading && (
					<div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
						<VisualContentLoopsTableView
							visualContentLoop={visualContentLoop}

							accountUUID={this.props.accountUUID}
							app={this.props.app}
							visualContentLoops={this.state.visualContentLoops}
							loadVisualContentLoop={this.loadVisualContentLoop}
							editLoopModal={this.editLoopModal}
							createNewLoop={this.createNewLoop}
							deleteSingleLoop={this.deleteSingleLoop}
							deleteSelectedLoops={this.deleteSelectedLoops}
							openLoopAssignments={this.openLoopAssignments}
							closeAssignments={this.closeAssignments}
						/>
						{visualContentLoop && (
							<VisualContentLoopView
								visualContentLoop={visualContentLoop}
								openLoopAssignments={this.openLoopAssignments}
								accountUUID={this.props.accountUUID}
								app={this.props.app}
								systemGroupUUID={this.props.systemGroupUUID}
								// fd={{}}
								// slideshow={slideshow}
								// editLoop={this.editLoop}
							/>
						)}
					</div>
				)}
			</div>
		);
	}
}

if (!window.CrowFormField) {
	window.CrowFormField = {};
}
window.CrowFormField['custom.vulture.visual_content_loops'] = CrowFieldCustomVultureVisualContentLoops;
export default CrowFieldCustomVultureVisualContentLoops;